import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { format } from "date-fns";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  debounce,
  FormControl,
  Grid,
  IconButton,
  InputBase,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Rating,
  Select,
  type SelectChangeEvent,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import type { GridColDef, GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import {
  AddCircleOutline,
  Call,
  DeleteOutline,
  Edit,
  Email,
  GridView,
  MoreVert,
  SearchOutlined,
  TableRows
} from "@mui/icons-material";

import { DataGrid } from "../../components/common/Index";
import apiService from "../../service/Api";
import { tokens } from "../../theme";
import { RootState } from "../../store/Index";
import { AddCrewFormProps, RoleSkillTool } from "./types/add-crew-member";

import EditCrewDialog from "./EditCrew";
import { AddCrewMemberDialog, DeletePilotDialog, SearchPilotDialog } from "./components";
import { MuiTelInputInfo } from "mui-tel-input";
import { UserProps } from "./types/search-pilot";
import { PilotAPIResponse, PilotProps } from "./types/view-pilot";
import { PaginationProps, Paginator } from "../../components/common/Paginator";
import { IStream } from "../stream-management/types/view-stream";
import { IAssignmentProps } from "./types/pilot-assignments";
import { AxiosError } from "axios";

interface JobsCompletedProps {
  user: number;
  pilot: number;
};

interface StatsProps {
  total: number;
  completed: number;
};

interface Params {
  page: number;
  page_size: number;
  search: string | undefined;
  status?: string | null;
};

const JobsCompleted: React.FC<JobsCompletedProps> = ({ user, pilot }) => {
  const [jobs, setJobs] = useState<StatsProps>({
    total: 0,
    completed: 0
  });
  const [jobsLoading, setJobsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchJobsStats = async () => {
      setJobsLoading(true);
      try {
        const jobs = await apiService.get<{
          count: number;
          next: string | null;
          previous: string | null;
          results: IAssignmentProps[];
        }>("/api/call-sheets/callsheet/",
          { crew_member: pilot });

        const completed = await apiService.get<{
          count: number;
          next: string | null;
          previous: string | null;
          results: IStream[];
        }>("/api/streams/stream/fullfilled-with-user/",
          { user_id: user });

        setJobs({
          total: jobs.data.count,
          completed: completed.data.count
        });
      } catch (err) {
        console.error("Failed to fetch jobs details:", err);
      } finally {
        setJobsLoading(false);
      }
    };

    fetchJobsStats();
  }, [pilot, user])

  if (jobsLoading) {
    return <CircularProgress size={12} sx={{ color: 'primary' }} />;
  };    
  return (
    <Box display={'flex'} flexDirection={'column'} sx={{ lineHeight: 1, py: 1 }}>
      <Typography variant="body2" fontWeight="medium">
        {`Assigned: ${jobs.total}`}
      </Typography>
      <Typography variant="body2" fontWeight="medium">
        {`Completed: ${jobs.completed}`}
      </Typography>
    </Box>
  );
};

const AllCrew: React.FC = () => {
  const [crew, setCrew] = useState<PilotProps[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>();
  const [selectedRoles, setSelectedRoles] = useState<RoleSkillTool[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<RoleSkillTool[]>([]);
  const [selectedTools, setSelectedTools] = useState<RoleSkillTool[]>([]);
  const [crewLoading, setCrewLoading] = useState(false);
  const [crewError, setCrewError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [searchMemberOpen, setSearchMemberOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<UserProps | null>();
  const [formLoading, setFormLoading] = useState(false);
  const [filterType, setFilterType] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"grid" | "table">("table");
  const [totalCrew, setTotalCrew] = useState(1);
  const [formData, setFormData] = useState<AddCrewFormProps>({
    existingUser: false,
    user_id: null,
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    password: "tilapia123",
    phone: "",
    role: "crew",
    organization: "",
    street_address: "",
    city: "",
    state: "",
    postal_code: "",
    residence_country: "",
    emergency_contact: "",
    emergency_contact_phone: "",
    date_of_birth: "1970-01-01",
    occupation: "",
    marital_status: 'single',
    national_id: "",
    roles: [],
    skills: [],
    tools: [],
    preferences: "",
    experience_years: 0
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleActionsMenuClose = () => {
    setAnchorEl(null);
  };

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedPilot, setSelectedPilot] = useState<{
    id: number | null;
    name: string | null;
  }>({id: null, name: null});
  
  const navigate = useNavigate();

  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const primaryColor = colors.primary[400];
  const primaryHoverColor = colors.primary[600];
  const primaryTextColor = colors.primary[400];
  const secondaryTextColor = colors.neutral[100];
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 0,
    pageSize: 10
  });

  const fetchCrewData = useCallback(async () => {
    setCrewLoading(true);
    setCrewError(null);
    setWarning(null);
    try {
      const apiPage = pagination.page + 1;
      const params: Params = {
        page: apiPage,
        page_size: pagination.pageSize,
        search: searchTerm || '',
      };
      if (filterType !== "all") {
        params.status = filterType;
      }

      const response = await apiService.get<{
        count: number;
        results: PilotAPIResponse[];
        next: string | null;
        previous: string | null;
      }>("/api/crews/crew-members/", params);

      setTotalCrew(response.data.count);

      const mappedPilots = response.data.results.map(
        (pilot: PilotAPIResponse): PilotProps => ({
          id: pilot.id,
          user_id: pilot.user.id,
          average_rating: pilot.average_rating,
          rating_count: pilot.rating_count,
          pilot_type: pilot.user.user_type,
          name: pilot.user.first_name || "",
          surname: pilot.user.last_name || "",
          email: pilot.user.email || "",
          username: pilot.user.username || "",
          phone: pilot.user?.phone_number || "",
          organization: pilot.user?.organization || "",
          date_of_birth: pilot.user.date_of_birth || "",
          role: pilot.user.role,
          department: pilot.roles_detail[0]?.name || "",
          status: "Available", // Need more logic for this
          avatar: pilot.user.profile_picture || "",
          joinDate: format(new Date(pilot.created_at), "dd MMM, yyyy"),
          totalEarnings: 0, // Not available in response, calculate these on the fly
          totalAssignments: 0, // Not available in response, calculate these on the fly
          averageRating: 0, // Not available in response, calculate these on the fly
          lastGig: "N/A", // Not available in response, calculate these on the fly
          nextGig: "N/A", // Not available in response, calculate these on the fly
          roles: pilot.roles,
          skills: pilot.skills,
          tools: pilot.tools,
          rolesDetail: pilot.roles_detail || [],
          skillsDetail: pilot.skills_detail || [],
          toolsDetail: pilot.tools_detail || [],
          documents: pilot.document_certifications || [], // Not available in response
          emergencyContact: pilot.user.emergency_contact_phone || "",
          emergencyContactName: pilot.user.emergency_contact || "",
          street_address: pilot.user.street_address || "",
          maritalStatus: pilot.user.marital_status || "",
          occupation: pilot.user.occupation || "",
          nationalId: pilot.user.national_id || "",
          city: pilot.user.city || "",
          state: pilot.user.state_province || "",
          postal_code: pilot.user.postal_code || "",
          country: pilot.user.residence_country || "",
          preferences: pilot.user.service_preferences || "",
          bio: pilot.bio || "",
          experience_years: pilot.experience_years || 0
        })
      );

      setCrew(mappedPilots);
      if (
        mappedPilots.some(
          (c) => (!c.name || !c.surname || !c.email))
      ) {
        setWarning(
          "Some pilots have missing information, which may affect display or search."
        );
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || "Failed to load pilots. Please try again.";
        setCrewError(errorMessage);
      } else {
        console.log({ error }, 'fetchCrew error');
      }
      } finally {
        setCrewLoading(false);
      }
    },
  [pagination, filterType, searchTerm]);

  useEffect(() => {
    fetchCrewData();
  }, [fetchCrewData]);

  const columns: GridColDef<PilotProps>[] = [
    {
      field: "name",
      headerName: "Crew Member",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", my: 1, alignItems: "center", gap: 1 }}>
          <Avatar
            src={params.row.avatar}
            sx={{
              width: 32,
              height: 32,
              bgcolor: colors.primary[800],
              color: colors.primary[100],
            }}
          />
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {`${params.row.name || ""} ${params.row.surname || ""}`.trim() || "Unnamed Crew Member"}
            </Typography>
            <Typography variant={'body2'} color={'text.secondary'}>
              {`ID: ${params.row.id}`}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "department",
      headerName: "Role & Status",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ my: 0.35 }}>
          <Typography sx={{ mb: -1.5 }} variant={'body2'}>
            {params.row.department}
          </Typography>
          <Chip
            label={getCrewStatusChipProps(params.row.status).label}
            size={'small'}
            sx={{
              bgcolor: getCrewStatusChipProps(params.row.status).bgcolor,
              color: getCrewStatusChipProps(params.row.status).color
            }}
          />                  
        </Box>
      ),
    },
    {
      field: "rating",
      headerName: "Rating",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ my: 1 }}>
          <Box display={'flex'} alignItems={'center'} gap={'2px'}>
            <Rating size="small" value={params.row.average_rating} precision={0.1} readOnly />
            <Typography variant={'body2'}>{params.row.average_rating}</Typography>
          </Box>
          <Typography variant={'body2'}>{`${params.row.rating_count} Review(s)`}</Typography>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Contact",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ my: 1 }}>
          <Typography variant={'body2'}>{params.row.email}</Typography>
          <Typography variant={'body2'}>{params.row.phone}</Typography>
        </Box>
      ),
    },
    {
      field: "jobs",
      headerName: "Jobs Assignments",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <JobsCompleted
            user={params.row.user_id}
            pilot={params.row.id}
          />
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", my: 1, gap: 1 }}>
          <Tooltip title={params.row.phone || "No phone number"}>
            <IconButton
              disabled={!params.row.phone}
              onClick={() =>
                params.row.phone &&
                window.open(`tel:${params.row.phone}`)
              }
              sx={{
                color: params.row.phone
                  ? primaryColor
                  : secondaryTextColor,
                opacity: params.row.phone ? 1 : 0.5,
                "&:hover": {
                  color: params.row.phone
                    ? primaryHoverColor
                    : secondaryTextColor,
                },
              }}
            >
              <Call fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.email || "No email"}>
            <IconButton
              disabled={!params.row.email}
              onClick={() =>
                params.row.email &&
                window.open(`mailto:${params.row.email}`)
              }
              sx={{
                color: params.row.email
                  ? primaryColor
                  : secondaryTextColor,
                opacity: params.row.email ? 1 : 0.5,
                "&:hover": {
                  color: params.row.email
                    ? primaryHoverColor
                    : secondaryTextColor,
                },
              }}
            >
              <Email fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <IconButton
            id="crew-actions-menu-button"
            aria-controls={open ? 'crew-actions-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={(event) => {
              event.stopPropagation();
              setSelectedPilot({
                id: params.row.id,
                name: `${params.row.name} ${params.row.surname}`
              });
              handleActionsMenuOpen(event);
            }}
            sx={{
              color: primaryColor,
              opacity: 1,
              "&:hover": {
                color: secondaryTextColor,
              },
            }}
          >
            <MoreVert fontSize={'small'} />
          </IconButton>
          <Menu
            id="crew-actions-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleActionsMenuClose}
            slotProps={{
              list: {
                'aria-labelledby': 'crew-actions-menu-button',
              },
            }}
          >
            <MenuItem
              onClick={() => {
                handleEditPilot();
                handleActionsMenuClose();
              }}>
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleDeletePilot();
                handleActionsMenuClose();
              }}>
                <ListItemIcon>
                  <DeleteOutline fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      ),
    },
  ];

  const handleEditPilot = () => {
    setOpenEditDialog(true);
  }

  const handleDeletePilot = () => {
    setOpenDeleteDialog(true);
  }

  const handleCloseDeleteDialog = () => {
    setSelectedPilot({
      id: null,
      name: null
    });
    setOpenDeleteDialog(false);
  };

  const handleCloseEditDialog = () => {
    setSelectedPilot({
      id: null,
      name: null
    });
    setOpenEditDialog(false);
  };

  const onDeleteConfirmation = async () => {
    if (selectedPilot) {
      try {
        await apiService.delete(`/api/crews/crew-members/${selectedPilot.id}/`);
        fetchCrewData();
        handleCloseDeleteDialog();
      } catch (err) {
        setCrewError("Failed to delete contact. Please try again.");
        console.error("Pilot Delete error:", err);
        handleCloseDeleteDialog();
      }
    }
  };

  const handleAddCrewMember = async () => {
    const {
      email,
      existingUser,
      date_of_birth,
      first_name,
      last_name,
      password,
      phone,
      roles,
      username
    } = formData;

    if (!first_name.length) {
      toast.error("Please provide a first name for this pilot.");
      return;
    }

    if (!last_name.length) {
      toast.error("Please provide a last name for this pilot.");
      return;
    }

    if (!email.length) {
      toast.error("Please provide an email for this pilot.");
      return;
    }

    if (!username.length) {
      toast.error("Please provide a username for this pilot.");
      return;
    }

    if (!phone) {
      toast.error("Please provide a phone number for this pilot.");
      return;
    }

    if (!roles.length) {
      toast.error("Please select at least 1 role for this pilot.");
      return;
    }

    if (!password.length) {
      toast.error("Please provide a password for this pilot.");
      return;
    }

    if (!date_of_birth) {
      toast.error("Please provide a date of birth for this pilot.");
      return;
    }

    let payload = {};
    if (existingUser) {
      payload = {
        user_id: formData.user_id,
        roles: formData.roles,
        tools: formData.skills,
        skills: formData.tools,
        experience_years: formData.experience_years,
        bio: "Pixel Pilot"
      };
    } else {
      payload = {
        user: {
          email: email,
          username: username,
          first_name: first_name,
          last_name: last_name,
          password: password,
          user_type: 'individual',
          phone_number: formData.phone,
          role: 'crew',
          organization: formData.organization,
          street_address: formData.street_address,
          city: formData.city,
          state_province: formData.state,
          postal_code: formData.postal_code,
          residence_country: formData.residence_country,
          date_of_birth: formData.date_of_birth,
          occupation: formData.occupation,
          marital_status: formData.marital_status,
          national_id: formData.national_id,
          emergency_contact: formData.emergency_contact,
          emergency_contact_phone: formData.emergency_contact_phone,
          assignment: selectedRoles[0].name,
          service_preferences: formData.preferences,
        },
        roles: formData.roles,
        tools: formData.skills,
        skills: formData.tools,
        experience_years: formData.experience_years,
        bio: "Pixel Pilot"
      };
    }

    setFormLoading(true);

    try {
      console.log({ payload });

      await apiService.post("/api/crews/crew-members/full-create/", payload);

      toast.success("Crew member added successfully!");
      resetForm();
      setAddMemberOpen(false);

      fetchCrewData();
    } catch (error) {
      if (error instanceof AxiosError) {
        const detail = error?.response?.data?.errors?.user_id ? error?.response?.data?.errors?.user_id[0] :
          error?.response?.data?.detail || "Failed to add crew member.";
        toast.error(detail);
      } else {
        console.error("Add crew error:", error);
      }
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      existingUser: false,
      user_id: null,
      first_name: "",
      last_name: "",
      email: "",
      username: "",
      password: "tilapia123",
      phone: "",
      role: "crew",
      organization: "",
      street_address: "",
      city: "",
      state: "",
      postal_code: "",
      residence_country: "",
      emergency_contact: "",
      emergency_contact_phone: "",
      date_of_birth: "",
      occupation: "",
      marital_status: 'single',
      national_id: "",
      roles: [],
      skills: [],
      tools: [],
      preferences: "",
      experience_years: 0
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // make these phone input handlers into one portable handler for dynamic fields
  const handleEmergencyContactChange = (value: string, info: MuiTelInputInfo) => {
    console.log({ value });
    setFormData((prev: AddCrewFormProps) => (
      { ...prev, emergency_contact_phone: `+${info.countryCallingCode}${info.nationalNumber}` }
    ));
  };

  // make these phone input handlers into one portable handler for dynamic fields
  const handlePhoneChange = (value: string, info: MuiTelInputInfo) => {
    console.log({ value });
    setFormData((prev: AddCrewFormProps) => (
      { ...prev, phone: `+${info.countryCallingCode}${info.nationalNumber}` }
    ));
  };

  const removeRole = (roleToRemove: number) => {
    setSelectedRoles(selectedRoles.filter((role) => role.id !== roleToRemove));
  };

  const removeSkill = (skillToRemove: number) => {
    setSelectedSkills(selectedSkills.filter((skill) => skill.id !== skillToRemove));
  };

  const removeTool = (toolToRemove: number) => {
    setSelectedTools(selectedTools.filter((tool) => tool.id !== toolToRemove));
  };

  const handleCountryChange = (country: string | null) => {
    setSelectedCountry(country);

    setFormData((prev: AddCrewFormProps) => ({
      ...prev,
      residence_country: country
    }));
  };

  const handleRolesChange = (roles: RoleSkillTool[]) => {
    if (selectedRoles.find(role => role.id === roles[roles.length - 1].id)) {
      toast.error(`The role ${roles[roles.length - 1].name} has already been selected`);
      return;
    }
    setSelectedRoles([ ...selectedRoles, roles[roles.length - 1]]);

    // eslint-disable-next-line prefer-const
    let roleIds: number[] = [];
    roles.forEach((role: RoleSkillTool) => {
      roleIds.push(role.id)
    });

    setFormData((prev: AddCrewFormProps) => ({
      ...prev,
      role: roles[0].name,
      roles: roleIds
    }));
  };

  const handleSkillsChange = (skills: RoleSkillTool[]) => {
    if (selectedSkills.find(skill => skill.id === skills[skills.length - 1].id)) {
      toast.error(`The skill ${skills[skills.length - 1].name} has already been selected`);
      return;
    }
    setSelectedSkills([ ...selectedSkills, skills[skills.length - 1]]);

    // eslint-disable-next-line prefer-const
    let skillIds: number[] = [];
    skills.forEach((skill: RoleSkillTool) => {
      skillIds.push(skill.id)
    });

    setFormData((prev: AddCrewFormProps) => (
      { ...prev, skills: skillIds }
    ));
  };

  const handleToolsChange = (tools: RoleSkillTool[]) => {
    if (selectedTools.find(tool => tool.id === tools[tools.length - 1].id)) {
      toast.error(`The tool ${tools[tools.length - 1].name} has already been selected`);
      return;
    }
    setSelectedTools([ ...selectedTools, tools[tools.length - 1]]);

    // eslint-disable-next-line prefer-const
    let toolIds: number[] = [];
    tools.forEach((tool: RoleSkillTool) => {
      toolIds.push(tool.id)
    });

    setFormData((prev: AddCrewFormProps) => (
      { ...prev, tools: toolIds }
    ));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const getCrewStatusChipProps = (status: string) => {
    switch (status.toLowerCase()) {
      case "busy":
        return {
          label: "Busy",
          bgcolor: "#F79E1B40",
          color: "#F79E1B",
        };
      case "available":
        return {
          label: "Available",
          bgcolor: "#4BA66340",
          color: "#4BA663",
        };
      case "onstream":
      return {
        label: "On Stream",
        bgcolor: "#0000FF26",
        color: "#0000FF",
      };
      default:
        return {
          label: "Unknown",
          bgcolor: "#BDBDBD40",
          color: "#BDBDBD",
        };
    }
  };

  // Refetching crew data if it fails
  const handleRetry = () => {
    fetchCrewData();
  };

  // Handling pagination when user changes the pagination model
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPagination(newModel);
  };

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.clear();
    };
  }, [debouncedSetSearchTerm]);

  const handleRowClick = (params: GridRowParams<PilotProps>) => {
    navigate(`/crew-management/view/${params.row.id}`);
  };

  if (!crew) return null;

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box sx={{ height: "100%", background: colors.primary[900], padding: "24px", borderRadius: 5, boxShadow: 0 }}>
        <Box display={'flex'} mb={'16px'} alignItems={'center'} justifyContent={'space-between'}>
          <Box display={'flex'} alignItems={'center'} gap={'4px'}>
            <Box
              sx={{
                backgroundColor: backgroundColor,
                border: '1px solid',
                borderRadius: 2,
                borderColor: 'primary',
                display: "flex",
                alignItems: "center",
                pl: 1,
                flex: 1,
                minWidth: 320,
              }}
            >
              <SearchOutlined sx={{ fontSize: 24, mr: 1, color: secondaryTextColor }} />
              <InputBase
                placeholder="Search by name, department or role..."
                sx={{ padding: .4, flex: 1, color: primaryTextColor }}
                onChange={(e) => debouncedSetSearchTerm(e.target.value)}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel
                id="crew-status-label"
                sx={{ color: primaryTextColor }}
              >
                Pilot Status
              </InputLabel>
              <Select
                labelId="crew-status-label"
                id="crew-status-label"
                value={filterType}
                label="Pilot Status"
                onChange={(e) => setFilterType(e.target.value as string)}
                sx={{
                  background: backgroundColor,
                  color: primaryTextColor,
                  borderRadius: 2,
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: primaryColor,
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="onstream">On Stream</MenuItem>
                <MenuItem value="busy">Unavailable</MenuItem>
              </Select>
            </FormControl>
            <Box display={'flex'} alignItems={'center'} gap={'4px'}>
              <Button
                size={"small"}
                disabled={viewMode === 'table'}
                startIcon={<TableRows />}
                sx={{ textTransform: 'capitalize', transition: '0.3s ease-in-out' }}
                onClick={() => setViewMode('table')}
                variant="contained">
                  Table
              </Button>
              <Button
                size={"small"}
                disabled={viewMode === 'grid'}
                startIcon={<GridView />}
                sx={{ textTransform: 'capitalize', transition: '0.3s ease-in-out' }}
                onClick={() => setViewMode('grid')}
                variant="contained">
                  Grid
              </Button>
            </Box>
          </Box>
          <Button
            size={"small"}
            startIcon={<AddCircleOutline />}
            onClick={() => setSearchMemberOpen(true)}
            sx={{ textTransform: 'capitalize' }}
            variant="contained">
              Add Crew
          </Button>
        </Box>

        {warning && (
          <Alert
            severity="warning"
            sx={{ mb: 2, color: primaryTextColor, bgcolor: backgroundColor }}
          >
            {warning}
          </Alert>
        )}

        {crewLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress sx={{ color: primaryColor }} />
          </Box>
        )}

        {crewError && (
          <Alert
            severity="error"
            sx={{ mb: 2, color: primaryTextColor, bgcolor: backgroundColor }}
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            }
          >
            {crewError}
          </Alert>
        )}

        {!crewLoading && !crewError && crew.length === 0 && (
          <Typography
            variant="h6"
            sx={{ color: secondaryTextColor, textAlign: "center", my: 4 }}
          >
            No pilots found.
          </Typography>
        )}

        <Box>
          {!crewLoading && !crewError &&
            crew.length > 0 &&
            viewMode === "table" && (
              <DataGrid
                rows={crew}
                columns={columns}
                rowCount={totalCrew}
                loading={crewLoading}
                paginationMode="server"
                paginationModel={pagination}
                onRowClick={handleRowClick}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[10, 25, 50, 100]}
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor:
                      colors.primary[500],
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor:
                      colors.primary[500],
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: colors.primary[900],
                    justifyContent: "center",
                    padding: 1,
                  },
                }}
              />
          )}

          {!crewLoading && !crewError &&
            crew.length > 0 && pagination &&
            viewMode === "grid" && (
              <Grid container spacing={2}>
                {crew.map((member) => (
                  <Grid size={{ xs: 12, md: 3 }} key={member.id}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        boxShadow: 0,
                        p: 2,
                        minHeight: 300,
                        bgcolor: backgroundColor,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        position: "relative",
                        "&:hover .menu-button": {
                          opacity: 1,
                        },
                      }}
                    >
                      <IconButton
                        id="crew-actions-menu-button"
                        aria-controls={open ? 'crew-actions-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedPilot({
                            id: member.id,
                            name: `${member.name} ${member.surname}`
                          });
                          handleActionsMenuOpen(event);
                        }}
                        className="menu-button"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          opacity: 0,
                          transition: "opacity 0.2s ease-in-out",
                          zIndex: 1,
                          bgcolor: "rgba(255, 255, 255, 0.8)",
                          "&:hover": {
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                          },
                        }}
                      >
                        <MoreVert sx={{ color: primaryColor }} />
                      </IconButton>

                      <Menu
                        id="crew-actions-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleActionsMenuClose}
                        slotProps={{
                          list: {
                            'aria-labelledby': 'crew-actions-menu-button',
                          },
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            handleEditPilot();
                            handleActionsMenuClose();
                          }}>
                            <ListItemIcon>
                              <Edit fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Edit</ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleDeletePilot();
                            handleActionsMenuClose();
                          }}>
                            <ListItemIcon>
                              <DeleteOutline fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Delete</ListItemText>
                        </MenuItem>
                      </Menu>
  
                      <CardContent
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          pb: 1,
                        }}
                      >
                        <Avatar
                          src={member.avatar || undefined}
                          alt={`${member.name} ${member.surname}`}
                          sx={{
                            width: 80,
                            height: 80,
                            mb: 2,
                            bgcolor: primaryColor,
                          }}
                        />
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          align="center"
                          sx={{ color: primaryTextColor, mb: 0.5 }}
                        >
                          {`${member.name || ""} ${member.surname || ""}`.trim() || "Unnamed Crew Member"}
                        </Typography>
                        <Typography
                          variant="body2"
                          align="center"
                          sx={{ mb: 0.5, color: secondaryTextColor }}
                        >
                          {member.department}
                        </Typography>
                        <Rating size="small" value={member.average_rating} precision={0.1} readOnly />
                      </CardContent>
  
                      <CardActions
                        sx={{
                          justifyContent: "center",
                          pt: 0,
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="View Contact">
                            <Button
                              onClick={() => navigate(`/crew-management/view/${member.id}`)}
                              sx={{
                                color: colors.primary[900],
                                backgroundColor: primaryColor,
                              }}
                            >
                              View Crew Member
                            </Button>
                          </Tooltip>
                        </Box>
  
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title={member.phone || "No phone number"}>
                            <IconButton
                              disabled={!member.phone}
                              onClick={() =>
                                member.phone &&
                                window.open(`tel:${member.phone}`)
                              }
                              sx={{
                                color: member.phone
                                  ? primaryColor
                                  : secondaryTextColor,
                                opacity: member.phone ? 1 : 0.5,
                                "&:hover": {
                                  color: member.phone
                                    ? primaryHoverColor
                                    : secondaryTextColor,
                                },
                              }}
                            >
                              <Call fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={member.email || "No email"}>
                            <IconButton
                              disabled={!member.email}
                              onClick={() =>
                                member.email &&
                                window.open(`mailto:${member.email}`)
                              }
                              sx={{
                                color: member.email
                                  ? primaryColor
                                  : secondaryTextColor,
                                opacity: member.email ? 1 : 0.5,
                                "&:hover": {
                                  color: member.email
                                    ? primaryHoverColor
                                    : secondaryTextColor,
                                },
                              }}
                            >
                              <Email fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          {(pagination && viewMode === "grid") ? (
            <Paginator
              count={totalCrew}
              pagination={pagination}
              setPagination={setPagination}
            />
          ) : null}
        </Box>
      </Box>

      {/* All Dialogs */}

      {selectedPilot && (
        <EditCrewDialog
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          pilot={crew.find(pilot => pilot.id === selectedPilot.id)}
        />
      )}

      <DeletePilotDialog
        openDeleteModal={openDeleteDialog}
        handleCloseDeleteDialog={handleCloseDeleteDialog}
        selectedPilot={selectedPilot}
        onDeleteConfirmation={onDeleteConfirmation}
      />
      
      <SearchPilotDialog
        searchMemberOpen={searchMemberOpen}
        setSearchMemberOpen={setSearchMemberOpen}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        setAddMemberOpen={setAddMemberOpen}
      />

      <AddCrewMemberDialog
        addMemberOpen={addMemberOpen}
        formData={formData}
        user={searchResults}
        formLoading={formLoading}
        handleAddCrewMember={handleAddCrewMember}
        handleEmergencyContactChange={handleEmergencyContactChange}
        handleInputChange={handleInputChange}
        handlePhoneChange={handlePhoneChange}
        handleSelectChange={handleSelectChange}
        onCountryChange={handleCountryChange}
        onRolesChange={handleRolesChange}
        onSkillsChange={handleSkillsChange}
        onToolsChange={handleToolsChange}
        removeRole={removeRole}
        removeSkill={removeSkill}
        removeTool={removeTool}
        resetForm={resetForm}
        selectedCountry={selectedCountry}
        selectedRoles={selectedRoles}
        selectedSkills={selectedSkills}
        selectedTools={selectedTools}
        setAddMemberOpen={setAddMemberOpen}
        setFormData={setFormData}
      />
    </Box>
  );
};

export default AllCrew;
